'use client';

import { c as _c } from "react/compiler-runtime";
import { parseLocalDateString, toLocalDateString } from '@bridge/utils';
import { useSearchParams } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import { DatePicker, FlexContainer } from '../../components';
import { Body } from '../../typography';
import { useTableFilters } from '../useTableFilters';
import type { ComponentProps } from 'react';
import type { BaseTableFilterProps } from './types';
type SingleDatePickerProps = Exclude<ComponentProps<typeof DatePicker>, {
  mode: 'range';
}>;

/**
 * A single-day filter. The URL holds the local calendar day as YYYY-MM-DD so links read naturally;
 * convert it for the API in `buildQueryParams` (e.g. `parseLocalDateString(day)?.toISOString()`).
 */
export interface DateTableFilterProps extends BaseTableFilterProps {
  searchParam: string;
  variant: 'date';
  placeholder?: string;
  /**
   * The day shown while the URL holds no valid day. `sanitizeSearchParams` passes it through as the
   * filter's value too, so the query applies what the picker shows. A function so a relative default
   * (e.g. `getStartOfPreviousWeek`) is computed when read rather than at module load.
   */
  getDefaultValue?: () => Date;
  /** Disables (rather than hides) the picker for the current URL, e.g. while another filter makes it moot. */
  isDisabled?: (searchParams: URLSearchParams) => boolean;
  componentProps?: Partial<Omit<SingleDatePickerProps, 'mode' | 'onSelect' | 'selected'>>;
  filterOptions?: never;
  groups?: never;
  value?: never;
  fromDateParam?: never;
  toDateParam?: never;
}

/** Renders `label` beside the picker: a bare date doesn't say what it bounds. */
export function DateTableFilter(t0) {
  const $ = _c(26);
  const {
    className,
    label,
    searchParam,
    searchParamsToClearOnChange,
    placeholder,
    getDefaultValue,
    isDisabled,
    componentProps,
    filterType: t1
  } = t0;
  const filterType = t1 === undefined ? "rsc" : t1;
  const searchParams = useSearchParams();
  const {
    updateFilterValue
  } = useTableFilters(filterType);
  let t2;
  if ($[0] !== getDefaultValue || $[1] !== searchParam || $[2] !== searchParams) {
    t2 = parseLocalDateString(searchParams?.get(searchParam)) ?? getDefaultValue?.();
    $[0] = getDefaultValue;
    $[1] = searchParam;
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selected = t2;
  let t3;
  if ($[4] !== isDisabled || $[5] !== searchParams) {
    t3 = isDisabled?.(searchParams ?? new URLSearchParams()) ?? false;
    $[4] = isDisabled;
    $[5] = searchParams;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const disabled = t3;
  let t4;
  if ($[7] !== searchParam || $[8] !== searchParamsToClearOnChange || $[9] !== updateFilterValue) {
    t4 = function handleSelect(date) {
      updateFilterValue(searchParam, date ? toLocalDateString(date) : null, searchParamsToClearOnChange);
    };
    $[7] = searchParam;
    $[8] = searchParamsToClearOnChange;
    $[9] = updateFilterValue;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleSelect = t4;
  let t5;
  if ($[11] !== className) {
    t5 = twMerge("gap-2", className);
    $[11] = className;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== label) {
    t6 = label ? <Body className="whitespace-nowrap text-content-secondary" size="sm">{label}</Body> : null;
    $[13] = label;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = componentProps?.["aria-label"] ?? label ?? placeholder ?? searchParam;
  let t8;
  if ($[15] !== componentProps || $[16] !== disabled || $[17] !== handleSelect || $[18] !== placeholder || $[19] !== selected || $[20] !== t7) {
    t8 = <DatePicker disabled={disabled} {...componentProps} aria-label={t7} mode="single" onSelect={handleSelect} placeholder={placeholder} selected={selected} />;
    $[15] = componentProps;
    $[16] = disabled;
    $[17] = handleSelect;
    $[18] = placeholder;
    $[19] = selected;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t5 || $[23] !== t6 || $[24] !== t8) {
    t9 = <FlexContainer className={t5} items="center">{t6}{t8}</FlexContainer>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}